import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, Tag, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DateRangePicker } from '@/components/DateRangePicker';
import { ExportButton } from '@/components/ExportButton';

const formatINR = (v) => {
  if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v?.toLocaleString('en-IN') || '0';
};

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="text-xs font-heading font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: p.color }} />
          {p.name}: {typeof p.value === 'number' ? p.value.toLocaleString('en-IN') : p.value}
        </p>
      ))}
    </div>
  );
}

export default function SchemesPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ scheme_data: [], kpis: {}, categories: [] });
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category) params.category = category;
        if (search) params.search = search;
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        const res = await api.get('/schemes', { params });
        setData(res.data);
      } catch (err) {
        toast.error('Failed to load schemes data');
      } finally {
        setLoading(false);
      }
    };
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [api, category, search, dateFrom, dateTo]);

  const k = data.kpis || {};

  // Show message when no sales data
  if (data.message) {
    return (
      <div className="space-y-6" data-testid="schemes-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Schemes</h1>
          <p className="text-sm text-muted-foreground mt-1">Scheme performance and margin analysis</p>
        </div>
        <div className="bg-card border border-border rounded-md p-12 text-center">
          <Package size={40} strokeWidth={1} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading font-semibold text-lg mb-2">No Sales Data Available</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">{data.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="schemes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Schemes</h1>
          <p className="text-sm text-muted-foreground mt-1">Scheme performance and margin analysis</p>
        </div>
        <div className="flex items-center gap-2">
          {/* No presets here: this page reads the primary billing upload, which has only ever held
              one month, so "Last 6 months" and "This month to date" would answer the same thing. */}
          <DateRangePicker dateFrom={dateFrom} dateTo={dateTo} presets={false}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} />
          <ExportButton module="schemes" params={{ category, date_from: dateFrom, date_to: dateTo }} />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-44" data-testid="scheme-filter-category"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>{data.categories?.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search product..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 w-56" data-testid="scheme-search" />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Total Sales</p>
          <p className="text-lg font-heading font-bold mt-1">INR {formatINR(k.total_sales || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Primary Offer</p>
          <p className="text-lg font-heading font-bold mt-1">INR {formatINR(k.total_primary_offer || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Retailer Margin</p>
          <p className="text-lg font-heading font-bold mt-1">INR {formatINR(k.total_retailer_margin || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Trade Scheme</p>
          <p className="text-lg font-heading font-bold mt-1">INR {formatINR(k.total_trade_scheme || 0)}</p>
        </div>
        <div className="bg-card border border-border rounded-md p-4">
          <p className="kpi-label text-muted-foreground">Distributor Margin</p>
          <p className="text-lg font-heading font-bold mt-1">INR {formatINR(k.total_distributor_margin || 0)}</p>
        </div>
      </div>

      {/* Chart + Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-md p-5">
          <h3 className="font-heading font-semibold text-sm mb-4">Top Products by Net Value</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.scheme_data?.slice(0, 10)} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={formatINR} stroke="hsl(var(--muted-foreground))" />
                <YAxis type="category" dataKey="material_desc" tick={{ fontSize: 8 }} width={150} stroke="hsl(var(--muted-foreground))" />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="total_net_value" name="Net Value" fill="#B8974A" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card border border-border rounded-md p-5 overflow-auto">
          <h3 className="font-heading font-semibold text-sm mb-4">Scheme Details</h3>
          {loading ? <p className="text-sm text-muted-foreground">Loading...</p> : (
            <div className="max-h-80 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-left text-xs sticky top-0 bg-card">Product</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Net Value</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">Qty</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">ZPCO%</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">ZRET%</TableHead>
                    <TableHead className="text-right text-xs sticky top-0 bg-card">ZDIS%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.scheme_data?.map((row, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1.5">
                          <Tag size={12} className="text-muted-foreground flex-shrink-0" />
                          <span className="truncate max-w-[160px]">{row.material_desc}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{formatINR(row.total_net_value)}</TableCell>
                      <TableCell className="text-right text-sm">{row.total_qty?.toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-right text-sm">{row.avg_zpco_pct}%</TableCell>
                      <TableCell className="text-right text-sm">{row.avg_zret_pct}%</TableCell>
                      <TableCell className="text-right text-sm">{row.avg_zdis_pct}%</TableCell>
                    </TableRow>
                  ))}
                  {(!data.scheme_data || data.scheme_data.length === 0) && (
                    <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No scheme data</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
